<template>
  <div>
    <div
      class="create-comment"
      :class="{
        errored: errors && errors.length,
        'show-editor': showEditor,
        ['boundary-' + formBoundary]: true,
        ['theme-' + theme]: true
      }"
    >
      <div class="d-flex justify-content-between align-items-center" v-if="!showEditor">
        <span
          id="addComment"
          class="text-secondary placeholder text-medium-1 flex-grow-1"
          role="button"
          @click="addAComment"
          >Add a comment ...</span
        >
        <subscribers
          v-if="showSubscribers"
          :org-id="orgId"
          :entity-type="entityType"
          :entity="entity"
        />
      </div>
      <div v-if="showEditor">
        <div class="editor-wrapper" v-no-capture>
          <ck-editor
            :theme="theme"
            ref="editor"
            :config="ckeditorConfig"
            :id="id"
            :initial-data="commentTemplate"
            @key="onKeyUp"
            @change="onChange"
            @save="$emit('save', $event)"
            @loaded="onEditorLoad"
          />
        </div>
        <div
          class="field-error-message"
          v-if="errors && errors.length > 0 && formBoundary === 'editor'"
        >
          {{ errors.join('. ') }}
        </div>
      </div>
      <span v-else></span>
      <!-- Adding an empty node so that submit buttons don't stick to top while ckeditor is not loaded -->
      <div class="d-flex justify-content-between align-items-center mt-3" v-if="showEditor">
        <submit-buttons
          class="mt-0"
          submit-text="Comment"
          :is-loading="isSaving"
          @save="onSave"
          @cancel="hideEditor"
        />
        <subscribers
          v-if="showSubscribers"
          :org-id="orgId"
          :entity-type="entityType"
          :entity="entity"
        />
      </div>
    </div>
    <div class="field-error-message" v-if="errors && errors.length > 0 && formBoundary === 'root'">
      {{ errors.join('. ') }}
    </div>
    <a href="javascript:void(0)" id="anchor"></a>
  </div>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import CkEditor from '@/components/CkEditor'
import SubmitButtons from '@/components/SubmitButtons'
import Subscribers from '@/components/Subscribers'
import { KEY_CODES } from '@/constants/keyCodes'
import editManager from '@/editManager'
import { commentStorageManager } from '@/helpers/autoSaveManager'
import getCkEditorPlainText from '@/utils/ckeditor/getCkEditorPlainText'
import showConfirm from '@/utils/showConfirm'

/**
 * This is the component for creating comment. It also show the list of subscribers - people who
 * will get notified about the created comment
 @emits
 - reset-errors
 - save: This is emitted when user types cmd + return in the editor
 - subscribe: When user is subscribed to the discussion thread
 - unsubscribe: When user is unsubscribed from the discussion thread
 */
export default {
  name: 'CreateComment',
  props: {
    /*
    Can be used to show errors regarding editor's content
     */
    errors: Array,
    /*
    Users that can be mentioned in editor
     */
    mentionableUsers: Array,
    orgId: [String, Number],
    focusAddCommentOnLoad: Boolean,
    isSaving: Boolean,
    entity: Object,
    theme: {
      default: 'dark'
    },
    /*
    This controls what should be contained inside border region
     */
    formBoundary: {
      default: 'root' // possible values - root | editor
    },
    entityType: String,
    showSubscribers: Boolean
  },
  data() {
    return {
      id: Math.random().toString(),
      showEditor: this.focusAddCommentOnLoad,
      commentTemplate: null,
      autoSaveKey: `${this.entityType}-${this.entity.id}`
    }
  },
  computed: {
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 150,
        startupFocus: true,
        users: this.mentionableUsers,
        orgId: this.orgId
      })
    }
  },
  components: {
    SubmitButtons,
    CkEditor,
    Subscribers
  },
  methods: {
    clearSavedData() {
      commentStorageManager.clearSavedContent(this.autoSaveKey)
    },
    onChange(data) {
      if (data) {
        if (!this.editId) this.editId = editManager.enterEditMode()
      } else if (this.editId) {
        editManager.exitEditMode(this.editId)
        this.editId = null
      }
      commentStorageManager.saveContent(this.autoSaveKey, data)
    },
    triggerCancellation(clearData) {
      this.showEditor = false
      this.commentTemplate = null
      clearData && this.clearSavedData()
      this.$emit('reset-errors')
    },
    hideEditor() {
      if (getCkEditorPlainText(this.$refs.editor)) {
        showConfirm({
          body: 'You have unsaved changes. Do you want to save them?',
          callback: this.triggerCancellation.bind(this, false),
          variant: 'primary',
          okTitle: 'Yes, save changes',
          cancelTitle: 'No, discard them',
          cancelCallback: this.triggerCancellation.bind(this, true)
        })
      } else {
        this.triggerCancellation(true)
      }
    },
    onSave() {
      this.clearSavedData()
      this.$emit('save', this.$refs.editor.getData())
    },
    onKeyUp(event) {
      if (event.keyCode === KEY_CODES.ESCAPE && !this.$refs.editor.getData()) {
        this.hideEditor()
        this.$el.querySelector('a#anchor').focus()
        //🖕 hack to focus the modal so that modal can be closed on esc. This is required on Activity Modals
      }
    },
    triggerReply(data) {
      const currentComment = commentStorageManager.getSavedContent(this.autoSaveKey)
      if (currentComment) {
        showConfirm({
          body:
            'You have an unsaved comment in progress. Are you sure you want to discard it and start this reply instead?',
          callback: this.loadCommentEditor.bind(this, data),
          variant: 'danger',
          okTitle: 'Yes, discard it'
        })
      } else {
        this.loadCommentEditor(data)
      }
    },
    loadCommentEditor(data) {
      this.commentTemplate = data
      if (!this.showEditor) {
        this.showEditor = true
      } else {
        this.$refs.editor.setData(data)
        setTimeout(() => {
          this.$refs.editor.moveCursorToTheEnd()
        }, 500) // Adding the delay to handle an edge case - selecting a comment and clicking on reply button was not bringing the editor to focus. Addition of delay unselects the selected text first, then brings the focus to editor.
      }
    },
    onEditorLoad() {
      if (this.commentTemplate) {
        this.$refs.editor.moveCursorToTheEnd()
      }
    },
    addAComment() {
      const savedContent = commentStorageManager.getSavedContent(this.autoSaveKey)
      this.loadCommentEditor(savedContent)
    }
  },
  watch: {
    showEditor(show) {
      if (!show && this.editId) {
        editManager.exitEditMode(this.editId)
        this.editId = null
      }
    }
  },
  beforeDestroy() {
    editManager.exitEditMode(this.editId)
  },
  mounted() {
    const savedContent = commentStorageManager.getSavedContent(this.autoSaveKey)
    if (savedContent) {
      this.loadCommentEditor(savedContent)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/errors';

.placeholder {
  cursor: text;
}

.create-comment {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  border-radius: 14px;

  &.errored {
    &.boundary-root {
      border: 1px solid $error;
    }

    &.boundary-editor .editor-wrapper {
      border: 1px solid $error;
    }
  }

  &.show-editor {
    min-height: 256px;
  }

  &.theme-dark {
    background: $quinary;
  }

  &.boundary-root {
    border: 1px solid $borderPrimary;
  }

  &.boundary-editor {
    .editor-wrapper {
      border: 1px solid $borderPrimary;
      border-radius: 10px;
      overflow: hidden;
      padding: 8px;
    }

    &.theme-light .editor-wrapper {
      background-color: $documentColor;
    }
  }
}
</style>
